import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  makeStyles,
  MenuItem,
  Select,
  TextField,
  Typography
} from "@material-ui/core";
import { WarningOutlined } from "@material-ui/icons";
import { AdhocAgreement, AdhocProject } from "integrity-owls-logic/dist";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { GRAY, GRAY_DARKEST, WHITE } from "../../../constants";
import adhocAgreementsService from "../../../services/AdhocAgreementsService";
import BasicTextField from "../../shared/BasicTextField";
import Confirm from "../../shared/Confirm";

interface AdhocAgreementFormProps {
  agreement?: AdhocAgreement;
  loading: boolean;
  onSubmit: (vals: any) => void;
}

const useStyles = makeStyles(() => ({
  dateField: {
    backgroundColor: GRAY_DARKEST,
    borderColor: GRAY_DARKEST,
    color: WHITE,
    fontSize: "16px",
    padding: "10px"
  },
  select: {
    backgroundColor: GRAY_DARKEST,
    padding: 10,
    borderRadius: 5,
    float: "right"
  }
}));

export const AdhocAgreementForm: React.FC<AdhocAgreementFormProps> = ({
  agreement,
  onSubmit,
  loading
}) => {
  const classes = useStyles();
  const history = useHistory();
  const [licenseeName, setLicenseeName] = useState("");
  const [songTitles, setSongTitles] = useState("");
  const [useDescription, setUseDescription] = useState("");
  const [media, setMedia] = useState("");
  const [territory, setTerritory] = useState("");
  const [term, setTerm] = useState("");
  const [licenseFee, setLicenseFee] = useState("");
  const [proRatedLicenseFee, setProRatedLicenseFee] = useState("");
  const [mfn, setMfn] = useState("");
  const [dateIssued, setDateIssued] = useState<string | undefined>("");
  const [notes, setNotes] = useState("");
  const [paymentTerm, setPaymentTerm] = useState("");
  const [paymentDueDate, setPaymentDueDate] = useState("");
  const [additionalProvisions, setAdditionalProvisions] = useState("");
  const [dateExecuted, setDateExecuted] = useState("");
  const [datePaid, setDatePaid] = useState("");
  const [adhocProjectId, setAdhocProjectId] = useState<string | undefined>("");
  const [status, setStatus] = useState("SENT_UNSIGNED");
  const [projects, setProjects] = useState<AdhocProject[]>([]);
  const [showVoidWarning, setShowVoidWarning] = useState(false);

  useEffect(() => {
    // TODO: move to context
    // fetch projects data
    const fetchProjects = async () => {
      const response = await adhocAgreementsService.getAdhocProjects({
        page: 0,
        rowsPerPage: -1
      });
      console.log("response", response);
      setProjects(response.data);
    };
    fetchProjects();
  }, [status]);

  console.log("projects", projects);

  useEffect(() => {
    const inputDateFormat = (date: Date) => {
      return date.toString().split("T")[0];
    };
    if (agreement) {
      setLicenseeName(agreement.licenseeName);
      setSongTitles(agreement.songTitles);
      setUseDescription(agreement.useDescription);
      setMedia(agreement.media);
      setTerritory(agreement.territory);
      setTerm(agreement.term);
      setLicenseFee(agreement.licenseFee);
      setProRatedLicenseFee(agreement.proRatedLicenseFee);
      setMfn(agreement.mfn);
      agreement.dateIssued &&
        setDateIssued(inputDateFormat(agreement.dateIssued));
      setPaymentTerm(agreement.paymentTerm);
      agreement.paymentDueDate &&
        setPaymentDueDate(inputDateFormat(agreement.paymentDueDate));
      agreement.additionalProvisions &&
        setAdditionalProvisions(agreement.additionalProvisions);
      agreement.dateExecuted &&
        setDateExecuted(inputDateFormat(agreement.dateExecuted));
      agreement.datePaid && setDatePaid(inputDateFormat(agreement.datePaid));
      agreement.adhocProjectId &&
        setAdhocProjectId(agreement.adhocProjectId.toString());
      agreement.notes && setNotes(agreement.notes);
      setStatus(agreement.status);
    }
  }, [agreement]);

  const handleSubmit = () => {
    const payload = {
      licenseeName,
      songTitles,
      useDescription,
      media,
      territory,
      term,
      licenseFee,
      proRatedLicenseFee,
      mfn,
      dateIssued: new Date(dateIssued || ""),
      paymentTerm,
      paymentDueDate: new Date(paymentDueDate || ""),
      additionalProvisions,
      dateExecuted: new Date(dateExecuted || ""),
      datePaid: new Date(datePaid || ""),
      adhocProjectId: adhocProjectId ? parseInt(adhocProjectId) : undefined,
      notes,
      status
    };
    if (agreement && agreement.id) {
      onSubmit({
        id: agreement.id,
        ...payload
      });
    } else {
      onSubmit(payload);
    }
  };

  const renderVoidWarning = () => {
    return (
      <Confirm
        record={agreement}
        loading={loading}
        open={showVoidWarning}
        title={
          <span>
            <WarningOutlined
              color="error"
              fontSize="large"
              style={{
                top: "8px",
                position: "relative"
              }}
            />
            <strong>Warning!</strong>
          </span>
        }
        content={
          <span>
            <strong>Agreement status is VOID</strong>
            <br />
            <span>
              You've marked this agreement as <strong>VOID</strong>. Are you
              sure you'd like to proceed?
            </span>
          </span>
        }
        onConfirm={() => handleSubmit()}
        onCancel={() => setShowVoidWarning(false)}
      />
    );
  };

  return (
    <form
      noValidate
      autoComplete="off"
      onSubmit={e => {
        e.preventDefault();
        if (status === "VOID" && agreement && agreement.id) {
          setShowVoidWarning(true);
        } else {
          handleSubmit();
        }
      }}
    >
      {renderVoidWarning()}
      <Box display="flex" flexDirection="row" mx={4} my={2}>
        <Box display="flex" flexDirection="column" mr={4}>
          <Box pb={1}>
            <Typography variant="overline">Licensee Name</Typography>
            <BasicTextField
              disabled={loading}
              fieldname="licenseeName"
              handleChange={e => setLicenseeName(e.currentTarget.value)}
              label="Licensee Name"
              value={licenseeName}
            />
          </Box>

          <Box pb={1}>
            <Typography variant="overline">Song Titles</Typography>
            <BasicTextField
              disabled={loading}
              fieldname="songTitles"
              handleChange={e => setSongTitles(e.currentTarget.value)}
              label="Song Titles"
              value={songTitles}
            />
          </Box>

          <Box pb={1}>
            <Typography variant="overline">Use Description</Typography>
            <BasicTextField
              disabled={loading}
              fieldname="useDescription"
              handleChange={e => setUseDescription(e.currentTarget.value)}
              label="Use Description"
              value={useDescription}
            />
          </Box>

          <Box pb={1}>
            <Typography variant="overline">Media</Typography>
            <BasicTextField
              disabled={loading}
              fieldname="media"
              handleChange={e => setMedia(e.currentTarget.value)}
              label="Media"
              value={media}
            />
          </Box>

          <Box pb={1}>
            <Typography variant="overline">Territory</Typography>
            <BasicTextField
              disabled={loading}
              fieldname="territory"
              handleChange={e => setTerritory(e.currentTarget.value)}
              label="Territory"
              value={territory}
            />
          </Box>
        </Box>

        <Box display="flex" flexDirection="column" mr={4}>
          <Box pb={1}>
            <Typography variant="overline">Term</Typography>
            <BasicTextField
              disabled={loading}
              fieldname="term"
              handleChange={e => setTerm(e.currentTarget.value)}
              label="Term"
              value={term}
            />
          </Box>
          <Box pb={1}>
            <Typography variant="overline">License Fee</Typography>
            <BasicTextField
              disabled={loading}
              fieldname="licenseFee"
              handleChange={e => setLicenseFee(e.currentTarget.value)}
              label="License Fee"
              value={licenseFee}
            />
          </Box>

          <Box pb={1}>
            <Typography variant="overline">Pro Rated License Fee</Typography>
            <BasicTextField
              disabled={loading}
              fieldname="proRatedLicenseFee"
              handleChange={e => setProRatedLicenseFee(e.currentTarget.value)}
              label="Pro Rated License Fee"
              value={proRatedLicenseFee}
            />
          </Box>

          <Box pb={1}>
            <Typography variant="overline">MFN</Typography>
            <BasicTextField
              disabled={loading}
              fieldname="mfn"
              handleChange={e => setMfn(e.currentTarget.value)}
              label="MFN"
              value={mfn}
            />
          </Box>

          <Box pb={1}>
            <Typography variant="overline">Payment Term</Typography>
            <BasicTextField
              disabled={loading}
              fieldname="paymentTerm"
              handleChange={e => setPaymentTerm(e.currentTarget.value)}
              label="Payment Term"
              value={paymentTerm}
            />
          </Box>
        </Box>
        <Box display="flex" flexDirection="column">
          <Box pb={1} display="flex" flexDirection="column">
            <Typography variant="overline">Date Issued</Typography>
            <TextField
              disabled={loading}
              type="date"
              className={classes.dateField}
              onChange={e => {
                e.preventDefault();
                setDateIssued((e.target as HTMLInputElement).value);
              }}
              value={dateIssued}
            />
          </Box>

          <Box pb={1} display="flex" flexDirection="column">
            <Typography variant="overline">Date Executed</Typography>
            <TextField
              disabled={loading}
              type="date"
              className={classes.dateField}
              onChange={e => {
                e.preventDefault();
                setDateExecuted((e.target as HTMLInputElement).value);
              }}
              value={dateExecuted}
            />
          </Box>

          <Box pb={1} display="flex" flexDirection="column">
            <Typography variant="overline">Payment Due Date</Typography>
            <TextField
              disabled={loading}
              type="date"
              className={classes.dateField}
              onChange={e => {
                e.preventDefault();
                setPaymentDueDate((e.target as HTMLInputElement).value);
              }}
              value={paymentDueDate}
            />
          </Box>

          <Box pb={1} display="flex" flexDirection="column">
            <Typography variant="overline">Date Paid</Typography>
            <TextField
              disabled={loading}
              type="date"
              className={classes.dateField}
              onChange={e => {
                e.preventDefault();
                setDatePaid((e.target as HTMLInputElement).value);
              }}
              value={datePaid}
            />
          </Box>

          <Box pb={1}>
            <FormControl>
              <Typography variant="overline">Adhoc Project</Typography>
              <Select
                disabled={loading}
                value={adhocProjectId}
                className={classes.select}
                onChange={e => {
                  e.preventDefault();
                  setAdhocProjectId(e.target.value as string);
                }}
              >
                {projects.map(project => (
                  <MenuItem value={project.id}>{project.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box pb={1}>
            <FormControl>
              <Typography variant="overline">Status</Typography>
              <Select
                disabled={loading}
                value={status}
                className={classes.select}
                onChange={e => {
                  e.preventDefault();
                  setStatus(e.target.value as string);
                }}
              >
                <MenuItem value="SENT_UNSIGNED">Sent Unsigned</MenuItem>
                <MenuItem value="EXECUTED_PENDING_PAYMENT">
                  Executed Pending Payment
                </MenuItem>
                <MenuItem value="PAID">Paid</MenuItem>
                <MenuItem value="VOID">Void</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          ml={4}
          justifyContent="space-between"
        >
          <Box pb={1}>
            <Typography variant="overline">Additional Provisions</Typography>
            <TextField
              disabled={loading}
              placeholder="Additional Provisions"
              style={{
                width: "100%",
                backgroundColor: GRAY_DARKEST,
                borderColor: GRAY_DARKEST,
                color: WHITE,
                fontSize: "16px",
                padding: "10px"
              }}
              multiline
              rows={8}
              onChange={e => {
                e.preventDefault();
                setAdditionalProvisions((e.target as HTMLInputElement).value);
              }}
              value={additionalProvisions}
            />
          </Box>

          <Box pb={1}>
            <Typography variant="overline">Notes</Typography>
            <TextField
              disabled={loading}
              placeholder="Notes"
              style={{
                width: "100%",
                backgroundColor: GRAY_DARKEST,
                borderColor: GRAY_DARKEST,
                color: WHITE,
                fontSize: "16px",
                padding: "10px"
              }}
              multiline
              rows={8}
              onChange={e => {
                e.preventDefault();
                setNotes((e.target as HTMLInputElement).value);
              }}
              value={notes}
            />
          </Box>

          <Box display="flex" flexDirection="row" justifyContent="flex-end">
            <Box mr={2}>
              <Button
                disabled={loading}
                onClick={() => history.push("/adhoc-agreements")}
                variant="contained"
                size="large"
                style={{ backgroundColor: GRAY }}
              >
                {loading ? <CircularProgress /> : "Cancel"}
              </Button>
            </Box>
            <Button
              disabled={loading}
              variant="contained"
              color="primary"
              type="submit"
              size="large"
            >
              {loading ? <CircularProgress /> : "Save"}
            </Button>
          </Box>
        </Box>
      </Box>
    </form>
  );
};

export default AdhocAgreementForm;
